<script>

    // -- The one local camera preview: renders the selected camera's raw track into a <video>,
    // -- driven entirely by the stores (camera_ready = track present && not muted, plus the
    // -- track store for in-place swaps: a device switch or lib-jitsi-meet's unmute re-acquire).
    // -- It renders a CLONE -- the base track keeps feeding the conference untouched, and the
    // -- clone is torn down with the preview so it can never keep the camera hot on its own.
    // -- (The in-session local tile deliberately stays a canvas pump -- see tile.svelte -- so
    // -- this component is only for plain "show me my camera" surfaces like the start panel.)

    import { onMount, onDestroy } from 'svelte';
    import { get } from 'svelte/store';

    import { camera_ready, getSelectedDeviceTrack, subscribeSelectedDeviceTrack } from '../../stores.js';

    // -- parent gates: active=false tears the preview down even when the camera is ready
    // -- (e.g. while a Join attempt is in flight); hidden only hides it (layout is kept)
    export let active = true;
    export let hidden = false;

    let video;
    let shown_track = null;

    $: active, refresh();

    const unsub_ready = camera_ready.subscribe(() => refresh());
    const unsub_track = subscribeSelectedDeviceTrack("camera", () => refresh());

    function refresh(){

        if (!video) return;

        const track = get(camera_ready) ? getSelectedDeviceTrack("camera") : null;

        if (active && track) _start(track);
        else _stop();
    }

    function _start(track){

        if (track === shown_track && video.srcObject) return;

        _stop();
        shown_track = track;
        video.srcObject = new MediaStream([track.clone()]);
        video.play().catch(error => {
            console.warn("[PL-CameraPreview] Failed to play camera preview ", error);
        });
    }

    function _stop(){

        shown_track = null;

        if (!video || !video.srcObject) return;

        video.srcObject.getVideoTracks().forEach(track => {
            track.stop();
            video.srcObject.removeTrack(track);
        });
        video.srcObject = null;
    }

    onMount(() => {
        _iosSettings();
        refresh();
    });

    onDestroy(() => {
        unsub_ready();
        unsub_track();
        _stop();
    });

    function _iosSettings(){

        if (!video) return;

        video.muted = true;
        video.controls = false;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('disablepictureinpicture', '');
        video.setAttribute('disableremoteplayback', '');
        video.setAttribute('x-webkit-airplay', 'deny');
        video.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback');
        if ('disablePictureInPicture' in video) {
            // @ts-ignore - Safari supports this at runtime
            video.disablePictureInPicture = true;
        }
        if ('disableRemotePlayback' in video) {
            try { video.disableRemotePlayback = true; } catch {}
        }
    }

</script>

<video style="visibility: {hidden ? 'hidden' : 'visible'};" class:live={shown_track} autoplay playsinline webkit-playsinline muted disablepictureinpicture bind:this={video}/>

<style>

    video {

        width: 55%;
        min-width: 20.5rem;

        /* -- fixed geometry from the first paint: with auto height the box renders at the
           -- replaced-element default until the (warming) camera delivers loadedmetadata,
           -- then jumps to size. 4/3 (taller than the 16:9 the constraints ask for) reads
           -- better as a self-view and matches iOS front cameras; a 16:9 feed gets its
           -- sides cropped by `cover` -- preview only, the published track is untouched. */
        aspect-ratio: 3 / 2;
        max-height: 100%;
        object-fit: cover;

        border-radius: 12px;

        transform: scaleX(-1);
    }

    /* -- black only while a track is attached: frames-pending reads as "camera starting",
       -- while a denied/muted/absent camera stays invisible instead of a dead black box */
    video.live {
        background: #000;
    }

</style>
